import React, { useEffect, useMemo, useRef, useState } from 'react'
import Layout from "../../components/Layout/Layout";
import { PaperAirplaneIcon, RefreshIcon, SearchIcon, InboxIcon, ChatAlt2Icon } from '@heroicons/react/solid';
import { ComplaintServices } from '../../services/CompliantServices';
import { DisplayTime, isToday, convert, pascalCASE } from './../../utils/convertDate';

const PAGE_SIZE = 20;

const statusBadges = {
    Open: "bg-red-100 text-red-700",
    New: "bg-blue-100 text-blue-700",
    "In Progress": "bg-amber-100 text-amber-700",
    Closed: "bg-green-100 text-green-700",
    Resolved: "bg-green-100 text-green-700",
};

function statusBadgeClass(status) {
    return statusBadges[status] || "bg-gray-100 text-gray-600";
}

const toTickets = (resp) => {
    const payload = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp?.data?.result)
                ? resp.data.result
                : resp?.data
                    ? [resp.data]
                    : [];
    return payload;
};

const toConversations = (resp) => {
    return Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp)
            ? resp
            : [];
};

export default function Complaint() {
    const [tickets, setTickets] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [search, setSearch] = useState("");
    const [activeTicketId, setActiveTicketId] = useState("");
    const [conversations, setConversations] = useState([]);
    const [customerDetails, setCustomerDetails] = useState({
        customer: {
            firstName: "",
            lastName: ""
        }
    });
    const [user, setUser] = useState({ firstName: "", surname: "" });
    const [message, setMessage] = useState("");
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [loadingThread, setLoadingThread] = useState(false);
    const [sending, setSending] = useState(false);
    const threadRef = useRef(null);
    const isMounted = useRef(true);

    const loadTickets = async () => {
        setLoadingTickets(true);
        const resp = await ComplaintServices.listComplaints();
        if (isMounted.current) {
            setTickets(toTickets(resp));
            setVisibleCount(PAGE_SIZE);
            setLoadingTickets(false);
        }
    }

    const refreshTickets = () => {
        if (loadingTickets) return;
        loadTickets();
    }

    useEffect(() => {
        isMounted.current = true;
        var details = sessionStorage.getItem("user");
        if (details) {
            try {
                setUser(JSON.parse(details));
            } catch (e) {
            }
        }
        loadTickets();
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [conversations, activeTicketId]);

    const visibleTickets = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = tickets.slice(0, visibleCount);
        if (!q) return list;
        return list.filter((t) =>
            (t.customer?.firstName || "").toLowerCase().includes(q) ||
            (t.customer?.lastName || "").toLowerCase().includes(q) ||
            (t.customer?.emailAddress || "").toLowerCase().includes(q) ||
            (t.subject || "").toLowerCase().includes(q)
        );
    }, [tickets, visibleCount, search]);

    const activeTicket = tickets.find((t) => t.ticketId === activeTicketId) || null;
    const remaining = Math.max(tickets.length - visibleCount, 0);

    const fetchTicketDetails = async (id) => {
        setLoadingThread(true);
        const [details, conversationsResp] = await Promise.all([
            ComplaintServices.getTicketDetails(id),
            ComplaintServices.listConversations(id),
        ]);
        if (!isMounted.current) return;
        if (details?.data) {
            setCustomerDetails(details.data);
        }
        setConversations(toConversations(conversationsResp));
        setLoadingThread(false);
    }

    const openTicket = async (id) => {
        if (activeTicketId === id) return;
        setActiveTicketId(id);
        setConversations([]);
        setCustomerDetails({ customer: { firstName: "", lastName: "" } });
        await fetchTicketDetails(id);
    }

    const refreshThread = async () => {
        if (!activeTicketId) return;
        setLoadingThread(true);
        const conversationsResp = await ComplaintServices.listConversations(activeTicketId);
        if (isMounted.current) {
            setConversations(toConversations(conversationsResp));
            setLoadingThread(false);
        }
    }

    const sendMessage = async () => {
        const text = message.trim();
        if (!text || !activeTicketId || sending) return;
        setSending(true);
        const resp = await ComplaintServices.addConversation({
            message: text,
            ticketId: activeTicketId
        });
        if (isMounted.current) {
            if (resp?.status) {
                setConversations((prev) => [
                    ...prev,
                    {
                        replyType: "Staff",
                        createdOn: new Date().toISOString(),
                        message: text
                    }
                ]);
                setTickets((prev) =>
                    prev.map((t) =>
                        t.ticketId === activeTicketId ? { ...t, ticketStatus: "Open" } : t
                    )
                );
                setMessage("");
            }
            setSending(false);
        }
    }

    const loadMore = () => setVisibleCount((c) => c + PAGE_SIZE);

    const detailsCustomer = customerDetails?.customer || {};
    const activeSubject = activeTicket?.subject || customerDetails?.subject || "";

    return (
        <Layout title="Complaints" current="/complaints" pageTag="compliant" fullWidth>
            <div className="h-[calc(100vh-4rem)] flex bg-gray-50">
                {/* Inbox pane */}
                <aside className="w-80 xl:w-96 shrink-0 bg-white border-r border-gray-200 flex flex-col">
                    <div className="px-4 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-gray-900">Inbox</h2>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={refreshTickets}
                                    disabled={loadingTickets}
                                    title="Refresh ticket list"
                                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <RefreshIcon className={`h-4 w-4 ${loadingTickets ? "animate-spin" : ""}`} aria-hidden="true" />
                                </button>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    {tickets.length} tickets
                                </span>
                            </div>
                        </div>
                        <div className="relative">
                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search customer, email or subject"
                                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto sc2">
                        {loadingTickets ? (
                            <div className="flex justify-center py-10">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                            </div>
                        ) : visibleTickets.length === 0 ? (
                            <div className="text-center py-10 px-6">
                                <InboxIcon className="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
                                <p className="mt-2 text-sm text-gray-500">{tickets.length ? "No tickets match your search" : "No complaints yet"}</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-100">
                                {visibleTickets.map((person) => {
                                    const isActive = person.ticketId === activeTicketId;
                                    return (
                                        <li key={person.ticketId}>
                                            <button
                                                onClick={() => openTicket(person.ticketId)}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${isActive ? "bg-indigo-50 ring-1 ring-inset ring-indigo-200" : ""}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`shrink-0 flex items-center justify-center h-10 w-10 rounded-full text-sm font-bold text-white ${isActive ? "bg-indigo-600" : "bg-gray-800"}`}>
                                                        {pascalCASE(person.customer?.firstName?.charAt(0) || "")} {pascalCASE(person.customer?.lastName?.charAt(0) || "")}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <p className="truncate text-sm font-semibold text-gray-900">
                                                                {pascalCASE(person.customer?.firstName)} {pascalCASE(person.customer?.lastName)}
                                                            </p>
                                                            <p className="shrink-0 text-xs text-gray-400">
                                                                {isToday(new Date(convert(person.createdAt))) ? DisplayTime(person.createdAt) : convert(person.createdAt)}
                                                            </p>
                                                        </div>
                                                        <p className="truncate text-sm text-gray-500">{person.subject}</p>
                                                        <div className="mt-1">
                                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadgeClass(person.ticketStatus)}`}>
                                                                {person.ticketStatus}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {remaining > 0 && (
                        <div className="px-4 py-3 border-t border-gray-200">
                            <button
                                onClick={loadMore}
                                className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                            >
                                Load more ({remaining} remaining)
                            </button>
                        </div>
                    )}
                </aside>

                {/* Conversation pane */}
                <section className="flex-1 min-w-0 flex flex-col bg-gray-50">
                    {!activeTicketId ? (
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <ChatAlt2Icon className="h-16 w-16 text-gray-300" aria-hidden="true" />
                            <p className="mt-4 text-lg font-medium text-gray-700">Select a ticket to start chatting</p>
                            <p className="mt-1 text-sm text-gray-400">Choose a conversation from the inbox</p>
                        </div>
                    ) : (
                        <>
                            <header className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
                                <div className="flex items-center justify-center h-11 w-11 rounded-full text-base font-bold text-white bg-gray-800">
                                    {detailsCustomer.firstName?.charAt(0)} {detailsCustomer.lastName?.charAt(0)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="text-base font-semibold text-gray-900 truncate">
                                            {pascalCASE(detailsCustomer.firstName)} {pascalCASE(detailsCustomer.lastName)}
                                        </p>
                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(activeTicket?.ticketStatus)}`}>
                                            {activeTicket?.ticketStatus}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500 truncate">
                                        {detailsCustomer.emailAddress} · {activeSubject}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="hidden md:inline-block text-xs text-gray-400">Ticket #{activeTicketId.slice(-6)}</span>
                                    <button
                                        onClick={refreshThread}
                                        title="Refresh messages"
                                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                    >
                                        <RefreshIcon className={`h-5 w-5 ${loadingThread ? "animate-spin" : ""}`} aria-hidden="true" />
                                    </button>
                                </div>
                            </header>

                            <div ref={threadRef} className="flex-1 min-h-0 overflow-y-auto sc2 px-6 py-6">
                                {loadingThread && conversations.length === 0 ? (
                                    <div className="flex justify-center py-10">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                                    </div>
                                ) : conversations.length === 0 ? (
                                    <div className="text-center py-10">
                                        <p className="text-sm text-gray-400">No messages yet. Start the conversation below.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {conversations.map((resp, i) => {
                                            const isStaff = resp.replyType === "Staff";
                                            return (
                                                <div key={i} className={`flex ${isStaff ? "justify-end" : "justify-start"}`}>
                                                    <div className={`flex max-w-[75%] ${isStaff ? "flex-row-reverse" : ""} items-end gap-2`}>
                                                        <div className="shrink-0 flex items-center justify-center h-8 w-8 rounded-full text-xs font-bold text-white bg-gray-700">
                                                            {isStaff
                                                                ? `${user.firstName?.charAt(0) || "A"} ${user.surname?.charAt(0) || ""}`
                                                                : `${detailsCustomer.firstName?.charAt(0) || ""} ${detailsCustomer.lastName?.charAt(0) || ""}`}
                                                        </div>
                                                        <div className={isStaff ? "flex flex-col items-end" : "flex flex-col items-start"}>
                                                            <div className={`px-4 py-2.5 text-sm rounded-2xl shadow-sm ${isStaff ? "bg-white border border-gray-200 text-gray-900 rounded-br-sm" : "bg-indigo-600 text-white rounded-bl-sm"}`}>
                                                                {resp.message}
                                                            </div>
                                                            <span className="mt-1 text-xs text-gray-400">{DisplayTime(resp.createdOn)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <footer className="shrink-0 bg-white border-t border-gray-200 px-4 py-3">
                                <div className="flex items-end gap-2">
                                    <textarea
                                        rows={1}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        placeholder="Type a message... (Enter to send)"
                                        className="flex-1 resize-none rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <button
                                        onClick={sendMessage}
                                        disabled={!message.trim() || sending}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {sending ? (
                                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                                        ) : (
                                            <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
                                        )}
                                        Send
                                    </button>
                                </div>
                            </footer>
                        </>
                    )}
                </section>
            </div>
        </Layout>
    )
}